import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './SponsorManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddTariffsModal from './AddTariffsModal';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import TableFilter from '../../components/TableFilter';
import { 
  FaPlus, 
  FaTimes, 
  FaEdit, 
  FaTrash, 
  FaUsers, 
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaBriefcase,
  FaDollarSign,
  FaExchangeAlt,
  FaFilePdf,
  FaFileExcel,
  FaAngleDoubleRight,
  FaFlask
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import './SponsorManagementDarkMode.css';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

// Projesi silinmiş tarifeler için ortak etiket. Ham doküman kimliği yerine bu
// gösterilir; böylece proje listesinde tek bir okunabilir girdi olarak toplanır.
const DELETED_PROJECT_LABEL = 'Silinmiş proje';

// Test verileri yalnızca ana admin tarafından görülebilir
const MAIN_ADMIN_EMAIL = 'turanemre1999@gmail.com';

// isTestProject bazı kayıtlarda boolean, bazılarında 'true' metni olarak
// saklanıyor (bkz. ProjectsManagementPage); iki biçim de kabul ediliyor.
const isTestProjectFlag = (project) =>
  project?.isTestProject === true || project?.isTestProject === 'true';

// "1.234,56" / "1234.56" gibi farklı biçimleri güvenle sayıya çevirir
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

// FTE değerinin parasal karşılığı: tarife değeri x birim fiyat
const calculateAmount = (tariff) => toNumber(tariff.value) * toNumber(tariff.price);

const formatAmount = (amount, currency) => {
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
  return currency ? `${formatted} ${currency}` : formatted;
};

const compareText = (a, b) =>
  String(a || '').localeCompare(String(b || ''), 'tr', { sensitivity: 'base' });

// Tablo sıralaması: müşteri > proje > tarife tipi > tarife değeri (sayısal),
// eşitlik durumunda en yeni tarih üstte kalır
const compareTariffs = (a, b) => {
  const customerDiff = compareText(a.customerName, b.customerName);
  if (customerDiff !== 0) return customerDiff;

  const projectDiff = compareText(a.projectName, b.projectName);
  if (projectDiff !== 0) return projectDiff;

  const typeDiff = compareText(a.type, b.type);
  if (typeDiff !== 0) return typeDiff;

  const valueDiff = toNumber(a.value) - toNumber(b.value);
  if (valueDiff !== 0) return valueDiff;

  return new Date(b.date || 0) - new Date(a.date || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: tr });
  } catch (error) {
    return dateString;
  }
};

const SponsorManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const { userEmail } = useUserProfile();
  const isMainAdmin =
    String(userEmail || '').trim().toLowerCase() === MAIN_ADMIN_EMAIL;
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [tariffModalKey, setTariffModalKey] = useState(0); // Add key for modal remounting
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    customerName: true,
    projectName: true,
    type: true,
    value: true,
    price: true,
    amount: true,
    currency: true,
    date: true,
    isSubcontractor: true,
    info: true
  });
  const [projectFilter, setProjectFilter] = useState('');
  const [showProjectsWithoutTariffs, setShowProjectsWithoutTariffs] = useState(false);
  // Test projelerine ait kayıtlar varsayılan olarak gizli; yalnızca ana admin açabilir
  const [showTestProjects, setShowTestProjects] = useState(false);
  const [projectsWithoutTariffs, setProjectsWithoutTariffs] = useState([]);
  const [preselectedCustomer, setPreselectedCustomer] = useState(null);
  const [preselectedProject, setPreselectedProject] = useState(null);

  // Pagination states - sayfalama istemci tarafında yapılır, böylece
  // istenen sayfaya doğrudan atlanabilir
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pageInput, setPageInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allTariffs, setAllTariffs] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);
  const tableRef = useRef(null);
  
  const columns = {
    customerName: { label: 'Müşteri', type: 'text' },
    projectName: { label: 'Proje', type: 'text' },
    type: { label: 'Tarife Tipi', type: 'text' },
    value: { label: 'Tarife Değer', type: 'text' },
    price: { label: 'Birim Fiyat', type: 'text' },
    amount: { label: 'Tutar (Değer x Fiyat)', type: 'text' },
    currency: { 
      label: 'Döviz Tipi', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "TRY", label: "TRY" },
        { value: "USD", label: "USD" },
        { value: "EUR", label: "EUR" }
      ]
    },
    date: { label: 'Tarih', type: 'date' },
    isSubcontractor: {
      label: 'Subcontractor',
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Evet", label: "Evet" },
        { value: "Hayır", label: "Hayır" }
      ]
    },
    info: { label: 'Bilgi', type: 'text' }
  };

  // Fetch projects without tariffs
  const fetchProjectsWithoutTariffs = async () => {
    try {
      // Get customers data
      const customersSnapshot = await getDocs(collection(db, 'Customers'));
      const customerIdMap = {};
      customersSnapshot.docs.forEach(doc => {
        customerIdMap[doc.data().Başlık] = doc.id;
      });

      // Get projects data
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      const allProjects = [];
      projectsSnapshot.docs.forEach(doc => {
        const projectData = doc.data();
        allProjects.push({
          id: doc.id,
          titleShort: projectData.titleShort,
          customer: projectData.customer,
          customerId: customerIdMap[projectData.customer] || null,
          customerName: projectData.customer,
          isTestProject: isTestProjectFlag(projectData)
        });
      });

      // Get all tariffs
      const allTariffsSnapshot = await getDocs(collection(db, 'Tariffs'));
      const projectIdsWithTariffs = new Set(allTariffsSnapshot.docs.map(doc => doc.data().project));

      // Find projects without tariffs
      const projectsWithoutTariffsData = allProjects.filter(
        project => !projectIdsWithTariffs.has(project.id)
      );
      setProjectsWithoutTariffs(projectsWithoutTariffsData);
    } catch (error) {
      console.error('Error fetching projects without tariffs:', error);
      toast.error('Projeler yüklenirken bir hata oluştu');
    }
  };

  // Müşteri, proje ve tarife verisini tek seferde çeker.
  // NOT: orderBy('date') bilerek kullanılmıyor - Firestore, orderBy alanı olmayan
  // dokümanları sonuçtan düşürüyor. Sıralama istemci tarafında yapılıyor.
  const fetchAllTariffs = async () => {
    setIsLoading(true);
    try {
      const [customersSnapshot, projectsSnapshot, tariffsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Projects')),
        getDocs(collection(db, 'Tariffs')),
      ]);

      const customersMap = {};
      customersSnapshot.docs.forEach(docSnap => {
        customersMap[docSnap.id] = docSnap.data().Başlık || docSnap.data()['Müşteri Adı'];
      });

      const projectsMap = {};
      const testProjectIds = new Set();
      projectsSnapshot.docs.forEach(docSnap => {
        const projectData = docSnap.data();
        projectsMap[docSnap.id] = projectData.titleShort;
        if (isTestProjectFlag(projectData)) testProjectIds.add(docSnap.id);
      });

      const tariffsData = tariffsSnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        // Bazı tarifeler silinmiş projelere işaret ediyor. Eskiden bu durumda
        // ham doküman kimliği ("5j0msgxlk48HdPQlqJUH") proje adı olarak
        // gösteriliyor ve proje listesini kirletiyordu. Artık hepsi tek bir
        // "Silinmiş proje" etiketinde toplanıyor; kayıt gizlenmiyor, yalnızca
        // okunabilir hale geliyor. (Adı tamamen rakamdan oluşan gerçek
        // projeler -- 219867, 223646 gibi -- bundan etkilenmez.)
        const projectTitle = projectsMap[data.project];
        const projectMissing = Boolean(data.project) && !projectTitle;
        const enriched = {
          id: docSnap.id,
          ...data,
          customerName: customersMap[data.customer] || data.customer,
          projectName: projectMissing
            ? DELETED_PROJECT_LABEL
            : (projectTitle || data.project),
          projectMissing,
          isTestProject: testProjectIds.has(data.project),
        };
        // FTE değerinin parasal karşılığı tabloda ve çıktılarda kullanılır
        enriched.amountValue = calculateAmount(enriched);
        enriched.amount = formatAmount(enriched.amountValue, enriched.currency);
        return enriched;
      });

      tariffsData.sort(compareTariffs);

      setAllTariffs(tariffsData);
      return tariffsData;
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Tarifeler yüklenirken bir hata oluştu');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh tariffs
  const refreshTariffs = async () => {
    setCurrentPage(1);
    await fetchAllTariffs();
    await fetchProjectsWithoutTariffs();
  };

  useEffect(() => {
    // Eski sürümden kalan sayfa önbelleği artık kullanılmıyor
    try {
      localStorage.removeItem('sponsorTariffsPageCache');
    } catch (e) {
      // sessizce yoksay
    }

    fetchAllTariffs();
    fetchProjectsWithoutTariffs();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  // Test projelerinin tarifeleri listeden tamamen çıkarılır; yalnızca ana admin
  // "Test Projelerini Göster" düğmesiyle bunları listeye dahil edebilir.
  const visibleTariffs = useMemo(
    () =>
      isMainAdmin && showTestProjects
        ? allTariffs
        : allTariffs.filter(tariff => !tariff.isTestProject),
    [allTariffs, isMainAdmin, showTestProjects]
  );

  const totalTariffs = visibleTariffs.length;

  const visibleProjectsWithoutTariffs = useMemo(
    () =>
      isMainAdmin && showTestProjects
        ? projectsWithoutTariffs
        : projectsWithoutTariffs.filter(project => !project.isTestProject),
    [projectsWithoutTariffs, isMainAdmin, showTestProjects]
  );

  useEffect(() => {
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      if (filters[key] && typeof filters[key] === 'string' &&
          key !== 'currency' && key !== 'isSubcontractor') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    setIsFilterActive(Boolean(hasActiveSearch || hasActiveFilters || projectFilter));

    // Kaynak her zaman görüntülenebilir tarife listesidir
    let filtered = visibleTariffs;

    // Proje listesinden seçim (tam eşleşme)
    if (projectFilter) {
      filtered = filtered.filter(tariff => tariff.projectName === projectFilter);
    }

    // Apply search term first (only if 3+ characters)
    if (hasActiveSearch) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        tariff =>
          (tariff.customerName && tariff.customerName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.projectName && tariff.projectName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.type && tariff.type.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.value && tariff.value.toString().toLowerCase().includes(lowercasedSearch)) ||
          (tariff.info && tariff.info.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        // For text filters, apply 3-character minimum (except select filters)
        if (typeof filters[key] === 'string' &&
            key !== 'currency' &&
            key !== 'isSubcontractor') {
          if (filters[key].trim().length >= 3) {
            filtered = filtered.filter(tariff =>
              String(tariff[key] || '')
                .toLowerCase()
                .includes(filters[key].toLowerCase())
            );
          }
        } else {
          // For select filters, apply immediately
          filtered = filtered.filter(tariff =>
            String(tariff[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredTariffs(filtered);
  }, [visibleTariffs, filters, searchTerm, projectFilter]);

  // Filtre/arama değiştiğinde ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, projectFilter, showTestProjects]);

  // Listbox için benzersiz proje adları
  const projectOptions = useMemo(() => {
    const names = new Set();
    visibleTariffs.forEach(tariff => {
      if (tariff.projectName) names.add(String(tariff.projectName));
    });
    // "Silinmiş proje" gerçek bir proje adı değil; alfabetik sıraya karışmasın
    // diye listenin sonuna alınıyor.
    return [...names].sort((a, b) => {
      if (a === DELETED_PROJECT_LABEL) return 1;
      if (b === DELETED_PROJECT_LABEL) return -1;
      return a.localeCompare(b, 'tr');
    });
  }, [visibleTariffs]);

  // Filtrelenmiş listenin toplam parasal karşılığı (döviz bazında)
  const amountTotals = useMemo(() => {
    const totals = {};
    filteredTariffs.forEach(tariff => {
      const currency = tariff.currency || '-';
      totals[currency] = (totals[currency] || 0) + (tariff.amountValue || 0);
    });
    return totals;
  }, [filteredTariffs]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setProjectFilter('');
    setIsFilterActive(false);
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const activeCount = totalTariffs; // Tüm tarifeler aktif kabul ediliyor
  const inactiveCount = 0; // No inactive tariffs for simplicity

  const handleEdit = (tariff) => {
    setSelectedTariff(tariff);
    setTariffModalKey(prev => prev + 1); // Increment key to force remount
    setShowTariffModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu tarifeyi silmek istediğinize emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'Tariffs', id));
        toast.success('Tarife başarıyla silindi');
        await refreshTariffs();
      } catch (error) {
        console.error('Error deleting tariff:', error);
        toast.error('Tarife silinirken bir hata oluştu');
      }
    }
  };

  // Sayfalama, filtrelenmiş sonuç kümesi üzerinden hesaplanır
  const totalPages = Math.max(1, Math.ceil(filteredTariffs.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedTariffs = filteredTariffs.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const goToNextPage = () => goToPage(safePage + 1);
  const goToPreviousPage = () => goToPage(safePage - 1);

  // Manuel sayfa atlama: kullanıcı sayfa numarasını yazıp doğrudan gidebilir
  const handlePageJump = (e) => {
    e.preventDefault();
    const target = parseInt(pageInput, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(`Lütfen 1 ile ${totalPages} arasında bir sayfa numarası girin.`);
      return;
    }
    setCurrentPage(target);
    setPageInput('');
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [filteredTariffs]);

  // Çıktılarda kullanılacak veri: filtre/arama aktifse görünen sonuçlar,
  // değilse tüm liste
  const getExportData = () => (isFilterActive ? filteredTariffs : visibleTariffs);

  const exportFileName = (extension) => {
    const scope = projectFilter ? `_${projectFilter.replace(/[^\w-]+/g, '_')}` : '';
    const today = new Date().toISOString().split('T')[0];
    return `Tarifeler${scope}_${today}.${extension}`;
  };

  const EXPORT_COLUMNS = [
    { key: 'customerName', header: 'Müşteri', width: 22 },
    { key: 'projectName', header: 'Proje', width: 22 },
    { key: 'type', header: 'Tarife Tipi', width: 14 },
    { key: 'value', header: 'Tarife Değer', width: 14 },
    { key: 'price', header: 'Birim Fiyat', width: 14 },
    { key: 'amount', header: 'Tutar', width: 16 },
    { key: 'currency', header: 'Döviz Tipi', width: 12 },
    { key: 'date', header: 'Tarih', width: 18 },
    { key: 'isSubcontractor', header: 'Subcontractor', width: 15 },
    { key: 'info', header: 'Bilgi', width: 30 },
  ];

  const exportRowValue = (tariff, key) => {
    if (key === 'date') return formatDate(tariff.date);
    if (key === 'amount') return tariff.amountValue || 0;
    if (key === 'price' || key === 'value') return toNumber(tariff[key]);
    const value = tariff[key];
    return value === null || value === undefined || value === '' ? '-' : String(value);
  };

  const downloadExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        toast.info('Dışa aktarılacak tarife bulunmuyor.');
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Tarifeler');

      worksheet.columns = EXPORT_COLUMNS.map(col => ({
        header: col.header,
        key: col.key,
        width: col.width,
      }));

      dataToExport.forEach(tariff => {
        const row = {};
        EXPORT_COLUMNS.forEach(col => {
          row[col.key] = exportRowValue(tariff, col.key);
        });
        worksheet.addRow(row);
      });

      // Tutar ve fiyat sütunları sayısal biçimlendirilir
      worksheet.getColumn('amount').numFmt = '#,##0.00';
      worksheet.getColumn('price').numFmt = '#,##0.00';

      // Döviz bazında toplam satırları
      worksheet.addRow({});
      Object.entries(
        dataToExport.reduce((acc, tariff) => {
          const currency = tariff.currency || '-';
          acc[currency] = (acc[currency] || 0) + (tariff.amountValue || 0);
          return acc;
        }, {})
      ).forEach(([currency, total]) => {
        const totalRow = worksheet.addRow({
          currency,
          amount: total,
          projectName: `TOPLAM (${currency})`,
        });
        totalRow.font = { bold: true };
      });

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFileName('xlsx');
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Excel dosyası oluşturulurken bir hata oluştu');
    } finally {
      setIsExporting(false);
    }
  };

  const downloadPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        toast.info('Dışa aktarılacak tarife bulunmuyor.');
        return;
      }

      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const { registerTurkishFont, PDF_FONT_NAME } = await import('../../utils/pdfFont');

      const pdfDoc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const hasTurkishFont = await registerTurkishFont(pdfDoc);
      const fontName = hasTurkishFont ? PDF_FONT_NAME : 'helvetica';

      pdfDoc.setFont(fontName, 'normal');
      pdfDoc.setFontSize(14);
      pdfDoc.text('Alt Yüklenici / Tarife Listesi', 40, 40);

      pdfDoc.setFontSize(9);
      const scopeLine = projectFilter ? `Proje: ${projectFilter}` : 'Proje: Tümü';
      pdfDoc.text(
        `${scopeLine}   |   Kayıt: ${dataToExport.length}   |   Tarih: ${new Date().toLocaleDateString('tr-TR')}`,
        40,
        58
      );

      autoTable(pdfDoc, {
        startY: 72,
        head: [EXPORT_COLUMNS.map(col => col.header)],
        body: dataToExport.map(tariff =>
          EXPORT_COLUMNS.map(col =>
            col.key === 'amount'
              ? formatAmount(tariff.amountValue, '')
              : String(exportRowValue(tariff, col.key))
          )
        ),
        styles: { font: fontName, fontSize: 7, cellPadding: 3 },
        headStyles: { font: fontName, fillColor: [17, 24, 39], textColor: [229, 231, 235] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          4: { halign: 'right' },
          5: { halign: 'right' },
        },
        margin: { left: 40, right: 40 },
      });

      // Döviz bazında toplamlar
      const totals = dataToExport.reduce((acc, tariff) => {
        const currency = tariff.currency || '-';
        acc[currency] = (acc[currency] || 0) + (tariff.amountValue || 0);
        return acc;
      }, {});

      let cursorY = (pdfDoc.lastAutoTable?.finalY || 72) + 20;
      pdfDoc.setFontSize(10);
      Object.entries(totals).forEach(([currency, total]) => {
        pdfDoc.text(`TOPLAM (${currency}): ${formatAmount(total, currency)}`, 40, cursorY);
        cursorY += 14;
      });

      pdfDoc.save(exportFileName('pdf'));
      toast.success('PDF dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading PDF file:', error);
      toast.error('PDF dosyası oluşturulurken bir hata oluştu');
    } finally {
      setIsExporting(false);
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Add column resizing handlers
  const handleColumnResize = (e, columnKey) => {
    if (!resizingColumn) return;

    const table = tableContainerRef.current;
    if (!table) return;

    const th = table.querySelector(`th[data-column="${columnKey}"]`);
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    
    // Set minimum width to prevent columns from becoming too narrow
    if (newWidth >= 100) {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    }
  };

  const handleMouseDown = (e, columnKey) => {
    setResizingColumn(columnKey);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setResizingColumn(null);
  };

  const handleMouseMove = (e) => {
    if (resizingColumn) {
      handleColumnResize(e, resizingColumn);
    }
  };

  // Add effect to handle mouse events
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, handleMouseMove, handleMouseUp]); // Added missing dependencies

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaBriefcase className={styles.headerIcon} />
              Alt Yüklenici / Tarife Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.addButton} onClick={() => {
                setSelectedTariff(null);
                setPreselectedCustomer(null);
                setPreselectedProject(null);
                setTariffModalKey(prev => prev + 1); // Increment key to force remount
                setShowTariffModal(true);
              }}>
                <FaPlus className={styles.buttonIcon} /> Yeni Tarife Ekle
              </button>
              <button
                className={styles.projectsButton}
                onClick={() => setShowProjectsWithoutTariffs(!showProjectsWithoutTariffs)}
                title="Hiç tarife eklenmemiş projeleri göster"
              >
                <FaBriefcase className={styles.buttonIcon} />
                Tarifesiz Projeler
                {visibleProjectsWithoutTariffs.length > 0 && (
                  <span className={styles.projectBadge}>{visibleProjectsWithoutTariffs.length}</span>
                )}
              </button>
              {isMainAdmin && (
                <button
                  className={`${styles.testToggleButton} ${showTestProjects ? styles.testToggleActive : ''}`}
                  onClick={() => setShowTestProjects(prev => !prev)}
                  title={showTestProjects
                    ? 'Test projelerine ait tarifeleri listeden gizle'
                    : 'Test projelerine ait tarifeleri listeye dahil et'}
                >
                  <FaFlask className={styles.buttonIcon} />
                  {showTestProjects ? 'Test Projelerini Gizle' : 'Test Projelerini Göster'}
                </button>
              )}
              <button
                className={styles.downloadButton}
                onClick={downloadExcel}
                disabled={isExporting}
                title="Görüntülenen listeyi Excel olarak indir"
              >
                <FaFileExcel className={styles.buttonIcon} />
                {isExporting ? 'Hazırlanıyor...' : 'Excel'}
              </button>
              <button
                className={`${styles.downloadButton} ${styles.pdfButton}`}
                onClick={downloadPdf}
                disabled={isExporting}
                title="Görüntülenen listeyi PDF olarak indir"
              >
                <FaFilePdf className={styles.buttonIcon} />
                {isExporting ? 'Hazırlanıyor...' : 'PDF'}
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBriefcase />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalTariffs}</p>
                  <h3 className={styles.cardTitle}>Toplam Tarife</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaDollarSign />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Tarife</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaExchangeAlt />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Tarife</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="En az 3 karakter girin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>En az 3 karakter girin</span>
              )}
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.projectSelectContainer}>
              <label className={styles.projectSelectLabel} htmlFor="projectFilterSelect">
                Proje
              </label>
              <select
                id="projectFilterSelect"
                className={styles.projectSelect}
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">Tüm Projeler ({projectOptions.length})</option>
                {projectOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {projectFilter && (
                <button
                  className={styles.clearProjectButton}
                  onClick={() => setProjectFilter('')}
                  title="Proje filtresini temizle"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showProjectsWithoutTariffs && (
          <div className={`${styles.projectsWithoutTariffsSection} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.projectsHeader}>
              <h2 className={styles.projectsTitle}>
                <FaBriefcase className={styles.projectsIcon} />
                Hiç Tarife Eklenmemiş Projeler ({visibleProjectsWithoutTariffs.length})
              </h2>
              <button
                className={styles.closeProjectsButton}
                onClick={() => setShowProjectsWithoutTariffs(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className={styles.projectsList}>
              {visibleProjectsWithoutTariffs.length > 0 ? (
                <div className={styles.projectsGrid}>
                  {visibleProjectsWithoutTariffs.map(project => (
                    <div key={project.id} className={styles.projectCard}>
                      <div className={styles.projectCardHeader}>
                        <FaBriefcase className={styles.projectCardIcon} />
                        <h3 className={styles.projectCardTitle}>{project.titleShort}</h3>
                      </div>
                      <div className={styles.projectCardBody}>
                        <p className={styles.projectCardCustomer}>
                          <strong>Müşteri:</strong> {project.customerName}
                        </p>
                        <button
                          className={styles.addTariffButton}
                          onClick={() => {
                            setSelectedTariff(null);
                            setPreselectedCustomer(project.customerId);
                            setPreselectedProject(project.id);
                            setTariffModalKey(prev => prev + 1);
                            setShowTariffModal(true);
                          }}
                        >
                          <FaPlus /> Tarife Ekle
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noProjectsMessage}>
                  <FaBriefcase className={styles.noProjectsIcon} />
                  <p>Tüm projelere en az bir tarife eklenmiş!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedTariff && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedTariffInfo}>
              <h3 className={styles.selectedName}>
                {selectedTariff.type}: {selectedTariff.value}
              </h3>
              <span className={styles.tariffPrice}>
                {selectedTariff.price} {selectedTariff.currency}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.actionButton} ${styles.editButton}`} 
                onClick={() => handleEdit(selectedTariff)}
              >
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.deleteButton}`} 
                onClick={() => handleDelete(selectedTariff.id)}
              >
                <FaTrash className={styles.actionIcon} />
                <span>Sil</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.clearButton}`} 
                onClick={() => setSelectedTariff(null)}
              >
                <FaTimes className={styles.actionIcon} />
                <span>Seçimi Temizle</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.paginationContainer}>
            <button
              className={styles.paginationButton}
              onClick={goToPreviousPage}
              disabled={safePage === 1 || isLoading}
            >
              Önceki
            </button>

            <div className={styles.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - safePage) <= 1) return true;
                  if (page === 2 && safePage <= 3) return true;
                  if (page === totalPages - 1 && safePage >= totalPages - 2) return true;
                  return false;
                })
                .map((page, index, array) => {
                  if (index > 0 && page - array[index - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${page}`}>
                        <span className={styles.ellipsis}>...</span>
                        <button
                          className={`${styles.pageNumber} ${safePage === page ? styles.active : ''}`}
                          onClick={() => goToPage(page)}
                          disabled={isLoading}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  }
                  return (
                    <button
                      key={page}
                      className={`${styles.pageNumber} ${safePage === page ? styles.active : ''}`}
                      onClick={() => goToPage(page)}
                      disabled={isLoading}
                    >
                      {page}
                    </button>
                  );
                })}
            </div>

            <button
              className={styles.paginationButton}
              onClick={goToNextPage}
              disabled={safePage === totalPages || isLoading}
            >
              Sonraki
            </button>

            {/* Manuel sayfa atlama - 150. sayfaya tek tek gitmek yerine doğrudan geçiş */}
            <form className={styles.pageJumpForm} onSubmit={handlePageJump}>
              <label className={styles.pageJumpLabel} htmlFor="pageJumpInput">
                Sayfaya git
              </label>
              <input
                id="pageJumpInput"
                type="number"
                min={1}
                max={totalPages}
                className={styles.pageJumpInput}
                value={pageInput}
                placeholder={String(safePage)}
                onChange={(e) => setPageInput(e.target.value)}
              />
              <button type="submit" className={styles.pageJumpButton} disabled={isLoading}>
                <FaAngleDoubleRight />
              </button>
            </form>

            <span className={styles.pageInfo}>
              Sayfa {safePage} / {totalPages}
              {isFilterActive
                ? ` (${filteredTariffs.length} sonuç / ${totalTariffs} tarife)`
                : ` (Toplam ${totalTariffs} tarife)`}
            </span>

            {Object.keys(amountTotals).length > 0 && (
              <span className={styles.amountTotals}>
                Toplam tutar:{' '}
                {Object.entries(amountTotals)
                  .map(([currency, total]) => formatAmount(total, currency))
                  .join(' • ')}
              </span>
            )}
          </div>

          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table 
                  ref={tableRef}
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th 
                            key={key}
                            data-column={key}
                            className={`${resizingColumn === key ? styles.resizing : styles.resizable}`}
                            style={{ width: columnWidths[key] || 'auto' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                          >
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedTariffs.length > 0 ? (
                      pagedTariffs.map((tariff, index) => (
                        <tr 
                          key={tariff.id} 
                          onClick={() => setSelectedTariff(tariff)}
                          className={selectedTariff && selectedTariff.id === tariff.id ? styles.selectedRow : ''}
                        >
                          {Object.keys(columns).map(key => 
                            visibleColumns[key] && (
                              <td
                                key={key}
                                title={key === 'projectName' && tariff.projectMissing
                                  ? 'Bu tarifenin bağlı olduğu proje silinmiş'
                                  : tariff[key]}
                                className={key === 'projectName' && tariff.projectMissing
                                  ? styles.missingProjectCell
                                  : undefined}
                                style={{ width: columnWidths[key] || 'auto' }}
                              >
                                {key === 'date'
                                  ? formatDate(tariff[key])
                                  : key === 'amount'
                                    ? tariff.amount
                                    : truncateText(tariff[key])}
                              </td>
                            )
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td 
                          colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} 
                          className={styles.noDataCell}
                          style={{ width: '100%' }}
                        >
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun tarife bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      <AddTariffsModal
        key={tariffModalKey} // Add key prop
        show={showTariffModal}
        handleClose={async () => {
          setShowTariffModal(false);
          setPreselectedCustomer(null);
          setPreselectedProject(null);
          await refreshTariffs();
        }}
        selectedTariff={selectedTariff}
        preselectedCustomer={preselectedCustomer}
        preselectedProject={preselectedProject}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default SponsorManagementPage;